import { useEffect, useState, useCallback } from 'react';
import {
  Box, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, MenuItem, Select,
  FormControl, InputLabel, Grid, IconButton, Tooltip, SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as locales from 'date-fns/locale';
import { format as fnsFormat } from 'date-fns';
import { adminStyles } from '../../../theme/adminStyles';
import axios from 'axios';
import i18next, { getBrowserLanguage } from '../../../lang';

interface LoginLog {
  id: number;
  userId: string | null;
  loginResult: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  organizationName: string | null;
  level: string | null;
}

const LOGIN_RESULTS = ['', 'SUCCESS', 'INVALID_PASSWORD', 'IP_RESTRICTED', 'PENDING_APPROVAL', 'ACCESS_DENIED', 'USER_NOT_FOUND'];

const resultChipColor = (result: string): 'success' | 'error' | 'warning' | 'default' => {
  if (result === 'SUCCESS') return 'success';
  if (result === 'INVALID_PASSWORD' || result === 'ACCESS_DENIED') return 'error';
  if (result === 'IP_RESTRICTED' || result === 'PENDING_APPROVAL') return 'warning';
  return 'default';
};

const LoginHistoryTab = () => {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [userId, setUserId] = useState('');
  const [loginResult, setLoginResult] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      const params: Record<string, string | number> = { page, rowsPerPage };
      if (userId) params.userId = userId;
      if (loginResult) params.loginResult = loginResult;
      if (startDate) params.startDate = fnsFormat(startDate, 'yyyy-MM-dd') + ' 00:00:00';
      if (endDate) params.endDate = fnsFormat(endDate, 'yyyy-MM-dd') + ' 23:59:59';

      const res = await axios.get('/api/v1/admin/service-status/login-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch login logs', err);
    }
  }, [page, rowsPerPage, userId, loginResult, startDate, endDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const tzAbbr = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
    return d.toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const truncateUA = (ua: string | null) => {
    if (!ua) return '-';
    return ua.length > 60 ? ua.substring(0, 60) + '...' : ua;
  };

  const datePickerSx = { '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' } };

  return (
    <>
      <Paper sx={adminStyles.filterPaper}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth size="small"
              label={i18next.t('service_status.user_id_or_name')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{i18next.t('service_status.result')}</InputLabel>
              <Select
                value={loginResult}
                label={i18next.t('service_status.result')}
                onChange={(e: SelectChangeEvent) => { setLoginResult(e.target.value); setPage(0); }}
              >
                {LOGIN_RESULTS.map(r => (
                  <MenuItem key={r} value={r}>
                    {r === '' ? i18next.t('service_status.all') : r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('service_status.start_date')}
                value={startDate}
                maxDate={endDate || undefined}
                onChange={(v) => setStartDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx }, actionBar: { actions: ['clear'] } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('service_status.end_date')}
                value={endDate}
                minDate={startDate || undefined}
                onChange={(v) => setEndDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx }, actionBar: { actions: ['clear'] } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="primary" onClick={handleSearch}><SearchIcon /></IconButton>
              <IconButton onClick={() => { setUserId(''); setLoginResult(''); setStartDate(null); setEndDate(null); setPage(0); }}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={adminStyles.tablePaper}>
        <TableContainer sx={{ flex: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('service_status.datetime')} ({tzAbbr})</TableCell>
                <TableCell>{i18next.t('service_status.user_id')}</TableCell>
                <TableCell>{i18next.t('service_status.organization')}</TableCell>
                <TableCell>{i18next.t('service_status.result')}</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>User-Agent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDate(log.createdAt)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.userId || '-'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.organizationName || '-'}</TableCell>
                  <TableCell>
                    <Chip label={log.loginResult} size="small" color={resultChipColor(log.loginResult)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.ipAddress || '-'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={log.userAgent || ''}><span>{truncateUA(log.userAgent)}</span></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {i18next.t('service_status.no_data')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </>
  );
};

export default LoginHistoryTab;
